import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { CreatePricelistDto } from '../../common/PricePrgClient';
import { ProgressButton } from '../../common/ProgressButton';
import { FormAutocomplete, FormTextField } from '../../common/form';
import { RootState } from '../../store';
import { getAppSettings } from '../settings/redux/settingsSelectors';
import { getPricelistDialogState, getUnfilteredPricelists } from './redux/pricelistSelectors';
import { closeCreatePricelistDialog, createPricelist } from './redux/pricelistSlices';

const mapStateToProps = (state: RootState) => ({
    pricelists: getUnfilteredPricelists(state),
    pricelistDialogState: getPricelistDialogState(state),
    appSettings: getAppSettings(state)
});

type OwnProps = {
    closeDialog: () => void;
    parentPricelist: { id: number; name: string; currency: string } | null;
};
type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type CreatePricelistForm = Omit<CreatePricelistDto, 'mainCurrencyPricelistId'> & {
    mainCurrencyPricelistId?: number | null;
};

const CreatePricelistDialog = ({
    parentPricelist,
    pricelists,
    pricelistDialogState,
    appSettings: { defaultCurrency, availableCurrencies }
}: Props) => {
    const [pricelistRefs, mainCurrencyPricelistIds] = useMemo(() => {
        const pricelistList = Object.values(pricelists);

        return [
            pricelistList.map(x => x.pricelistRef.toUpperCase()),
            pricelistList
                .filter(x => x.mainCurrencyPricelistId == null && x.parentPricelistId == null)
                .sort((a, b) => a.pricelistRef.localeCompare(b.pricelistRef))
                .map(x => x.pricelistId)
        ];
    }, [pricelists]);

    const schema: yup.SchemaOf<CreatePricelistForm> = yup.object().shape({
        name: yup.string().max(40).required().label('Name'),
        parentPricelistId: yup.number().label('Parent pricelist Id'),
        currency: yup.string().required().label('Currency'),
        ref: yup
            .string()
            .max(10)
            .required()
            .test(
                'notOneOf',
                'Reference already exists',
                value => value == null || !pricelistRefs.includes(value.toUpperCase())
            )
            .label('Ref'),
        mainCurrencyPricelistId: yup.number().nullable().optional().label('Main currency pricelist')
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty }
    } = useForm<CreatePricelistDto>({
        resolver: yupResolver(schema),
        defaultValues: {
            currency: parentPricelist?.currency ?? defaultCurrency ?? '',
            parentPricelistId: parentPricelist?.id
        }
    });

    const dispatch = useDispatch();

    const submit = (form: CreatePricelistDto) => {
        dispatch(createPricelist.request(form));
    };

    const handleClose = () => {
        dispatch(closeCreatePricelistDialog());
    };

    return (
        <Dialog open={true}>
            <DialogTitle>
                Create new pricelist {parentPricelist != null ? ` below ${parentPricelist.name}` : ''}
            </DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    id="createPricelistForm"
                    sx={{ display: 'flex', flexDirection: 'column', px: 2, pb: 2, minWidth: 300 }}
                    onSubmit={handleSubmit(submit)}
                >
                    <FormTextField sx={{ my: 1 }} control={control} label="Name" name="name" autoFocus />
                    <FormTextField sx={{ my: 1 }} control={control} label="Reference" name="ref" />
                    <FormControl sx={{ my: 2 }} fullWidth>
                        <InputLabel id="currencyLabel">Currency</InputLabel>
                        <Controller
                            control={control}
                            name="currency"
                            render={({ field, fieldState: { error } }) => (
                                <Select
                                    labelId="currencyLabel"
                                    {...field}
                                    label="Currency"
                                    disabled={parentPricelist != null}
                                    error={!!error}
                                >
                                    {availableCurrencies.map(cur => (
                                        <MenuItem key={cur} value={cur}>
                                            {cur}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                    {parentPricelist == null && (
                        <FormAutocomplete
                            label="Main currency pricelist"
                            name="mainCurrencyPricelistId"
                            control={control}
                            options={mainCurrencyPricelistIds}
                            getOptionLabel={plId => {
                                const pricelist = pricelists[plId];
                                return pricelist
                                    ? `${pricelist.pricelistRef} - ${pricelist.name}`
                                    : String(plId);
                            }}
                        ></FormAutocomplete>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ProgressButton
                    color="primary"
                    type="submit"
                    form="createPricelistForm"
                    disabled={!isDirty}
                    isLoading={pricelistDialogState.isCreating}
                >
                    Create
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreatePricelistDialog);
